/**
 * useDeepLTranslated — 한국어 문자열(또는 배열)을 현재 UI 언어로 렌더 시점에 번역.
 *
 * 핵심 설계:
 *  - 번역 대상 언어는 **i18n.language**(화면에 실제로 보이는 UI 언어)를 단일 출처로 사용.
 *    · 과거엔 getCurrentBeLang()(localStorage 'vvt_lang')를 썼는데, 언어는 URL prefix(/en 등)로
 *      i18n 이 결정되는 반면 localStorage 는 따로 남아, "한국어 화면인데 localStorage=ENG" 처럼
 *      desync 되면 차량 스펙만 영어로 번역되는 버그가 있었음(t()/useMoney 는 i18n 기반이라 한국어).
 *    · i18n.language 로 통일 → t()/useMoney 와 항상 같은 언어. 언어 전환에도 반응형으로 재계산.
 *  - 부모가 매 렌더마다 새 배열/객체 참조를 넘겨도 **내용 기반 키**로 실제 텍스트 변경 시에만 재호출
 *  - 번역 결과를 (언어+원문)→번역 Map으로 보관 → 언어별 분리 캐시, 리렌더 중 원문 역행 방지
 *  - UI 언어가 ko면 항상 원문 반환 (다른 언어의 캐시가 역행해 노출되지 않음)
 *  - 한국어 미포함 텍스트는 원문 유지 (DeepL 호출 스킵)
 *  - translateBatch 내부 캐시가 중복 호출 차단
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBatch } from '../api/deepl';

const HANGUL_RE = /[ㄱ-ㆎ가-힣]/;
const SEP = String.fromCharCode(1); // 캐시/키 구분자 (실제 텍스트에 등장하지 않는 제어문자)

function needsTranslation(s) {
  return typeof s === 'string' && HANGUL_RE.test(s);
}

/**
 * @param {string | string[] | null | undefined} input
 * @returns 동일 타입으로 번역된 값. 번역 완료 전엔 원문 표시.
 */
export function useDeepLTranslated(input) {
  // 화면 UI 언어를 단일 출처로 — t()/useMoney 와 동일(react-i18next). 언어 전환에 반응형.
  const { i18n } = useTranslation();
  const beLang = (i18n.language || 'ko').split('-')[0];

  const isArray = Array.isArray(input);
  const list = useMemo(() => (isArray ? input : [input]), [input, isArray]);

  // 번역 대상 텍스트만 추출 (내용 기반 키 생성)
  const targetTexts = useMemo(
    () => list.filter((s) => needsTranslation(s)),
    [list]
  );
  // 언어를 키에 포함 → 언어 전환 시 effect 재실행 + 언어별 캐시 분리
  const contentKey = useMemo(
    () => beLang + SEP + targetTexts.join(SEP),
    [beLang, targetTexts]
  );

  // translation map을 state로 관리 → 업데이트 시 자동 리렌더 & useMemo 재계산
  const [transMap, setTransMap] = useState(() => new Map());

  useEffect(() => {
    if (beLang === 'ko' || targetTexts.length === 0) return;

    const missing = targetTexts.filter((t) => !transMap.has(beLang + SEP + t));
    if (missing.length === 0) return;

    let cancelled = false;
    translateBatch(missing, beLang).then((out) => {
      if (cancelled) return;
      let hasNew = false;
      const next = new Map(transMap);
      missing.forEach((src, i) => {
        const translated = out[i];
        const k = beLang + SEP + src;
        if (translated && !next.has(k)) {
          next.set(k, translated);
          hasNew = true;
        }
      });
      if (hasNew) setTransMap(next);
    });
    return () => { cancelled = true; };
  // contentKey(언어+원문)만으로 재실행 결정 — 참조 안정성 불필요
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  const translated = useMemo(() => {
    const out = list.map((s) => {
      if (!needsTranslation(s)) return s;
      if (beLang === 'ko') return s; // 한국어: 항상 원문(다른 언어 캐시 역행 방지)
      return transMap.get(beLang + SEP + s) || s;
    });
    return isArray ? out : out[0];
  }, [list, isArray, transMap, beLang]);

  return translated;
}
